import { hasModifierKey } from "@angular/cdk/keycodes";

const EXPANDED_SELECTOR = "[aria-expanded='true']";

const baselines = new WeakMap<Event, Map<HTMLElement, number>>();
const claimed = new WeakSet<Event>();

/**
 * An Escape press meant to dismiss something: not with a modifier, not a held key's auto-repeat
 * (which would dismiss a whole stack of dialogs before the user sees it), and not one cancelling
 * an IME composition (Safari reports that as key "Escape" with keyCode 229).
 */
export function isDismissEscape(event: KeyboardEvent): boolean {
  return (
    event.key === "Escape" &&
    !event.repeat &&
    !event.isComposing &&
    event.keyCode !== 229 &&
    !hasModifierKey(event)
  );
}

/**
 * Marks an Escape press as used up, so the containers around the target - a disableClose dialog
 * (DialogEscapeService), an appModalOverlay - don't also close on it. Unlike preventDefault() it
 * leaves the key's native behaviour alone (e.g. a search field clearing itself).
 */
export function claimEscape(event: Event): void {
  claimed.add(event);
}

/** Whether a handler already used this Escape press (preventDefault() or {@link claimEscape}). */
export function isEscapeClaimed(event: Event): boolean {
  return event.defaultPrevented || claimed.has(event);
}

/**
 * Tells whether an Escape key press was used up by something nested inside `root` (a dropdown,
 * a popover, a grabbed row, …), so the container around it - a dialog or an overlay - should not
 * close on the same press.
 *
 * Call it synchronously from the container's keydown handler and pass `onDecided`: it reports once
 * the event has finished propagating, because nested popups don't all handle Escape before the
 * container sees it - an appClickOutside dropdown listens on `document`, after every element.
 *
 * The press counts as used when a handler called preventDefault() or {@link claimEscape}, or when a
 * popup collapsed: `root` has fewer `aria-expanded="true"` elements than when the key went
 * down. That baseline is taken by {@link watchEscapeBaseline} in the capture phase, before any
 * handler (and the change detection each one triggers) has run.
 */
export function decideEscape(
  root: HTMLElement | null | undefined,
  event: KeyboardEvent,
  onDecided: (claimedInside: boolean) => void,
): void {
  const expandedBefore = (root && baselines.get(event)?.get(root)) ?? countExpanded(root);
  setTimeout(() => onDecided(isEscapeClaimed(event) || countExpanded(root) < expandedBefore));
}

/**
 * Records, in the capture phase of every Escape keydown under `target`, how many popups were
 * expanded in the container `rootOf` finds for the event. Returns a function that stops watching.
 */
export function watchEscapeBaseline(
  target: EventTarget,
  rootOf: (event: KeyboardEvent) => HTMLElement | null,
): () => void {
  const record = (event: Event) => {
    if (!(event instanceof KeyboardEvent) || event.key !== "Escape") {
      return;
    }
    const root = rootOf(event);
    if (!root) {
      return;
    }
    // One event can pass several watchers (an overlay inside a dialog), each for its own root.
    let counts = baselines.get(event);
    if (!counts) {
      counts = new Map();
      baselines.set(event, counts);
    }
    if (!counts.has(root)) {
      counts.set(root, countExpanded(root));
    }
  };
  target.addEventListener("keydown", record, true);
  return () => target.removeEventListener("keydown", record, true);
}

function countExpanded(root: HTMLElement | null | undefined): number {
  return root ? root.querySelectorAll(EXPANDED_SELECTOR).length : 0;
}
